import { z } from 'zod';
import type { PushSubscriptionJson } from '@awwyeah/shared';

const vapidKeyResponseSchema = z.object({ publicKey: z.string() });

export async function fetchVapidPublicKey(signal?: AbortSignal): Promise<string | null> {
  const res = await fetch('/api/push/vapid-public-key', { signal });
  if (res.status === 503) return null; // push not configured server-side yet
  if (!res.ok) throw new Error(`vapid key fetch failed: ${res.status}`);
  return vapidKeyResponseSchema.parse(await res.json()).publicKey;
}

/** The server derives the owning member from the session cookie, so nothing else is sent. */
export async function subscribeOnServer(subscription: PushSubscriptionJson): Promise<void> {
  const res = await fetch('/api/push/subscribe', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ subscription }),
  });
  if (!res.ok) throw new Error(`subscribe failed: ${res.status}`);
}

export async function unsubscribeOnServer(endpoint: string): Promise<void> {
  const res = await fetch('/api/push/subscribe', {
    method: 'DELETE',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ endpoint }),
  });
  if (!res.ok) throw new Error(`unsubscribe failed: ${res.status}`);
}

export async function sendTestPush(): Promise<void> {
  const res = await fetch('/api/push/test', { method: 'POST' });
  if (!res.ok) throw new Error(`test push failed: ${res.status}`);
}

/** Prod-safe test/poke (owner request 2026-08-04). `to: 'me'` pings your own devices;
 * `to: 'others'` sends a short line to every other member's. */
export async function sendPoke(to: 'me' | 'others', message?: string): Promise<{ sent: number }> {
  const res = await fetch('/api/push/poke', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ to, ...(message ? { message } : {}) }),
  });
  if (!res.ok)
    throw new Error(
      res.status === 429
        ? 'Easy there — try again in a few minutes.'
        : `poke failed: ${res.status}`,
    );
  return (await res.json()) as { sent: number };
}
